import jsPDF from 'jspdf'
import proj from 'ol/proj'
import { projection25830 } from '../../projs'

// import config from '../../../config'


const fileReader = new FileReader

/**
 * 
 * 
 * @export
 * @param {ol.Map} map
 * @param {Highcharts.chart} chart 
 * @param {any[]} data 
 * @param {any} {period, title, year, comments} 
 * @returns Promise<void>
 */
export default async function (map, chart, legendURL, layersLoad, { title = '', year = '', comments = ''}) {
  map.getTargetElement().style.pointerEvents = 'none'
  // console.log('setPointerEvents', map.getTargetElement())
  
  // Documento PDF
  const doc = new jsPDF()
  // Ancho y Alto del PDF
  const docWidth  = doc.internal.pageSize.getWidth(),
        docHeight = doc.internal.pageSize.getHeight()

  // Resolución a la que se exporta el mapa
  const resolution = 300 // dpi
  // Ancho y alto del mapa
  const w = docWidth - 10
  const h = docHeight / 5

  await drawMap.call(this, doc, map, layersLoad, resolution, w, h, 5, 50)
  await drawChart.call(this, doc, chart, resolution, w, h, 5, h + 50 + 5)
  await drawLegend.call(this, doc, legendURL, 10, 55)
  await drawLogos.call(this, doc)

  drawHeader.call(this, doc, map, title, year)
  drawComments.call(this, doc, comments, 7, (h * 2) + 50 + 10)
  drawFooter.call(this, doc, docWidth, 5, 0, docHeight - 5)

  doc.save('exported-map.pdf')
}

async function drawLegend(doc, legendURL, offsetX, offsetY) {
  if(typeof legendURL == 'object') {
    return await drawLegendGradient(doc, legendURL, offsetX, offsetY)
  }

  const base64 = await readImage(legendURL)
  const [w, h] = await getImageDimensions(base64)

  const ratio = w / h

  const mapHeight = doc.internal.pageSize.getHeight() / 5
  
  const height = mapHeight - (mapHeight * 0.5)
  const width = ratio * height

  doc.addImage(base64, 'PNG', offsetX, offsetY, width, height)
}

async function drawLegendGradient(doc, {minColor, maxColor, minValue, maxValue}, offsetX, offsetY) {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  const mapHeight = doc.internal.pageSize.getHeight() / 5

  canvas.width = 4
  canvas.height = mapHeight - (mapHeight * 0.5)

  const gradient = context.createLinearGradient(0, 0, 0, canvas.height)
  gradient.addColorStop(0, minColor)
  gradient.addColorStop(1, maxColor)

  context.fillStyle = gradient

  context.fillRect(0, 0, canvas.width, canvas.height)

  const canvasData = await readCanvas(canvas)
  const [w, h] = await getImageDimensions(canvasData)

  const fontSize = 10
  const { scaleFactor } = doc.internal
  const ratio = 1.15
  const minValueTextHeight = fontSize / (ratio * scaleFactor)

  doc.setFontType('bold')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(fontSize)
  
  doc.text(offsetX + w + 1, offsetY + minValueTextHeight + 1, minValue.toString())
  doc.text(offsetX + w + 1, offsetY + h - 1, maxValue.toString())

  doc.setFontType('normal')

  doc.addImage(canvasData, 'PNG', offsetX, offsetY, canvas.width, canvas.height)
}


function drawMap(doc, map, layersLoad, resolution, width, height, offsetX, offsetY) {

  // Ancho y alto del mapa para imprimir (Con la resolución indicada)
  const mapWidth = Math.round(width * resolution / 25.4)
  const mapHeight = Math.round(height * resolution / 25.4)

  // Ancho y alto del mapa (En píxeles)
  const size = map.getSize()
  // Bounding Box del mapa
  const extent = map.getView().calculateExtent()

  // Cambiar el extent del mapa para que se vea en el PDF
  const sizePrint = [mapWidth, mapHeight]

  const k = mapHeight / mapWidth

  const newH = size[0] * k

  map.getTargetElement().style.height = `${newH}px`

  map.setSize(sizePrint)
  map.getView().fit(extent)

  map.updateSize()
  map.renderSync()

  return new Promise(resolve => {
    map.once('postcompose', async event => {
      // Podemos acceder al canvas desde el evento postcompose
      const canvas = event.context.canvas

      // Esperamos a que se carguen todas los Tiles
      await waitTileLoad(layersLoad)
    
      // Obtenemos Base64 del mapa
      const canvasData = await readCanvas(canvas)

      doc.addImage(canvasData, 'PNG', offsetX, offsetY, width, height)

      // Dibujar líneas mapa
      doc.setDrawColor(0, 0, 0)
    
      doc.line(offsetX, offsetY, width + offsetX, offsetY)
      doc.line(offsetX, height + offsetY, width + offsetX, height + offsetY)
    
      doc.line(offsetX, offsetY, offsetX, height + offsetY)
      doc.line(width + offsetX, offsetY, width + offsetX, height + offsetY)

      // Volvemos el mapa al estado normal
      map.getTargetElement().style.width = '100%'
      map.getTargetElement().style.height = '100%'
      map.getTargetElement().style.pointerEvents = 'auto'

      map.setSize(size)
      map.getView().fit(extent)
      map.renderSync()

      resolve()
    })
  })

}

async function drawChart(doc, chart, resolution, width, height, offsetX, offsetY) {
  // Ancho y alto de la gráfica para imprimir (Con la resolución indicada)
  const chartWidth = Math.round(width * 0.5 * resolution / 25.4)
  const chartHeight = Math.round(height * 0.5 * resolution / 25.4)

  // SVG del gráfico de Highcharts
  const graph = chart.getSVG({
    exporting: {
      sourceWidth: chartWidth,
      sourceHeight: chartHeight,
    }
  })

  // Obtenemos un canvas de la gráfica a partir del SVG
  const graphCanvas = await svgToCanvas(graph, chartWidth, chartHeight)
  // Obtenemos Base64 de la gráfica
  const graphData = await readCanvas(graphCanvas)

  doc.addImage(graphData, 'PNG', offsetX, offsetY, width, height)
}

async function drawLogos(doc) {

  const docWidth = doc.internal.pageSize.getWidth()
  // Logos
  const logoHeight = 10
  const logoClient = await readImage('./static/img/client_logo.png')
  const logoGUD = await readImage('./static/img/gud_logo.png')

  const [wg, hg] = await getImageDimensions(logoGUD)
  const [wc, hc] = await getImageDimensions(logoClient)

  const kg = wg / hg
  const kc = wc / hc

  const wwg = logoHeight * kg
  const wwc = logoHeight * kc


  doc.addImage(logoGUD, 'PNG', docWidth - wwg - 5, 5, wwg, logoHeight)
  doc.addImage(logoClient, 'PNG', 5, 5, wwc, logoHeight)
}

function drawHeader(doc, map, title, year) {
  const docWidth = doc.internal.pageSize.getWidth()
  // Cajetín
  const logoHeight = 20
  const cajHeight = 25
  const offset = 3

  doc.setTextColor(0, 0, 0)

  doc.setFontSize(9)
  doc.setFontType('bold')

  doc.text(this.$t('titulo'), 5, logoHeight + offset)

  doc.text(this.$t('fecha'), 5, logoHeight + (cajHeight / 2) + offset)
  doc.text(`${this.$t('extension')} (${map.getView().getProjection().getCode()})`, 50, logoHeight + (cajHeight / 2) + offset)
  doc.text(this.$t('año-referencia'), docWidth - 5 - 35, logoHeight + offset)

  doc.setFontSize(8)
  doc.setFontType('normal')
  // console.log('title')
  doc.text(title, 7, logoHeight + offset + (offset * 1.5))
  // console.log('year')
  doc.text(year.toString(), docWidth - 5 - 33, logoHeight + offset + (offset * 1.5))
  // console.log('date')
  doc.text((new Date()).toLocaleString(), 7, logoHeight + (cajHeight / 2) + (offset * 2.5))


  const extent = proj.transformExtent(map.getView().calculateExtent(), map.getView().getProjection(), projection25830)
  // console.log('extension')
  doc.text('[' + extent.map(c => c.toFixed(3)).join(', ') + ']', 52, logoHeight + (cajHeight / 2) + (offset * 2.5))
}

function drawComments(doc, comments, offsetX, offsetY) {

  const docWidth = doc.internal.pageSize.getWidth()
  // comments
  doc.setFontSize(10)
  doc.setTextColor(0, 0, 0)
  doc.setFontType('bold')

  doc.text(this.$t('comentarios'), offsetX, offsetY)

  doc.line(offsetX, offsetY + 1.5, docWidth - offsetX, offsetY + 1.5)

  doc.setFontType('normal')
  doc.setFontSize(8)

  const splitComentarios = doc.splitTextToSize(comments, docWidth - 14)
  doc.text(offsetX, offsetY + 7, splitComentarios)
}

function drawFooter(doc, width, height, offsetX, offsetY) {
  doc.setFillColor(33, 33, 33)
    
  //Footer GUD
  doc.rect(offsetX, offsetY, width, height, 'F')

  doc.setTextColor(255, 255, 255)
  doc.text('© GreenUrbanData', 5, offsetY + 3.5)
}


function readCanvas(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      fileReader.onload = event => {
        const imgData = event.target.result
        resolve(imgData)
      }

      fileReader.onerror = reject
      fileReader.readAsDataURL(blob)
    }, 'image/png')
  })
}

function svgToCanvas(svg, w, h) {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  canvas.width = w
  canvas.height = h

  const img = document.createElement('img')

  return new Promise((resolve, reject) => {
    img.onload = () => {
      context.drawImage(img, 0, 0, w, h)
      resolve(canvas)
    }

    img.onerror = reject
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)))
  })

}

function fetchImage(url) {
  return fetch(url).then(res => res.blob())
}

async function readImage(url) {
  const blob = await fetchImage(url)

  return new Promise((resolve, reject) => {
    fileReader.readAsDataURL(blob)

    fileReader.onload = event => {
      const imgData = event.target.result
      resolve(imgData)
    }

    fileReader.onerror = reject
  })
}

function waitTileLoad(layersLoad) {
  const loadArray = layersLoad.map(() => [0, 0])

  let resolved = false

  return new Promise(res => {
    const tileLoadStart = (idx) => loadArray[idx][0]++
    const tileLoadEnd = (idx) => {
      loadArray[idx][1]++
      if (loadArray[idx][1] >= loadArray[idx][0]) {
        stopListenTileLoad(layersLoad[idx], tileLoadStart, tileLoadEnd)
      }

      if(loadArray.every(([loading, loaded]) => loaded == loading)) {
        setTimeout(res, 100)
        resolved = true
      }

    }

    layersLoad.forEach((l, idx)=> {
      l.getSource().on('tileloadstart', tileLoadStart.bind(null, idx))
      l.getSource().on(['tileloadend', 'tileloaderror'], tileLoadEnd.bind(null, idx))
    })

    setTimeout(() => {
      if(!resolved) {
        layersLoad.forEach(layer => stopListenTileLoad(layer, tileLoadStart, tileLoadEnd))
        res()
      }
    }, 5000)

  })

}

function stopListenTileLoad(layer, tileLoadStart, tileLoadEnd) {
  layer.getSource().un('tileloadstart', tileLoadStart)
  layer.getSource().un(['tileloadend', 'tileloaderror'], tileLoadEnd)
}

function getImageDimensions(data) {
  const image = document.createElement('img')
  image.src = data

  return new Promise(resolve => {
    image.onload = () => resolve([image.naturalWidth, image.naturalHeight])
  })
}


// WEBPACK FOOTER //
// ./src/components/core/search/print-report.js